'use client';
import React, { useState, ReactNode, ReactElement } from 'react';

import { cn } from '@/lib/utils/cn';

type TabsVariant = 'line' | 'button';
const inject = (children: ReactNode, injectedProps: Record<string, any>): ReactNode =>
  React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;

    return React.cloneElement(child as ReactElement<any>, {
      ...injectedProps,
      children: inject(
        (child as ReactElement<{ children?: ReactNode }>).props.children,
        injectedProps
      ),
    });
  });

type TabsProps = {
  defaultValue?: number;
  varient?: TabsVariant;
  className?: string;
  children: ReactNode;
};

const TabsRoot = ({ defaultValue = 0, varient = 'line', className, children }: TabsProps) => {
  const [activeTab, setActiveTab] = useState(defaultValue);

  return (
    <div className={cn('flex w-full flex-col', className)}>
      {inject(children, { activeTab, setActiveTab, varient })}
    </div>
  );
};

const TabsList = ({ children, varient }: { children: ReactNode; varient?: TabsVariant }) => (
  <div
    className={cn(
      'flex w-full',
      varient === 'line' && 'border-b border-border',
      varient === 'button' && 'gap-2'
    )}
  >
    {children}
  </div>
);

type TabsTriggerProps = {
  value: number;
  children: ReactNode;
  className?: string;
  activeTab?: number;
  setActiveTab?: (v: number) => void;
  varient?: TabsVariant;
  dataTestId?: string;
};

const TabsTrigger = ({
  value,
  children,
  className,
  activeTab = 0,
  setActiveTab,
  varient = 'line',
  dataTestId,
}: TabsTriggerProps) => {
  const isActive = activeTab === value;

  return (
    <button
      data-testid={dataTestId}
      onClick={() => setActiveTab?.(value)}
      className={cn(
        'group flex items-center gap-x-1 text-xs font-medium transition-all',
        className,
        varient === 'line' &&
          (isActive
            ? 'h-[48px] border-b-[3px] border-blue-1 px-3 text-blue-1'
            : 'h-[48px] border-b-[3px] border-transparent px-3 text-text hover:text-blue-1-hover'),
        varient === 'button' &&
          cn(
            'h-[34px] rounded-lg px-4',
            isActive ? 'bg-blue-1 text-white' : 'bg-background text-text hover:bg-hover'
          )
      )}
    >
      {children}
    </button>
  );
};
type TabsContentProps = {
  value: number;
  children: ReactNode;
  activeTab?: number;
  className?: string;
};

const TabsContent = ({ value, children, activeTab = 0, className }: TabsContentProps) => {
  if (activeTab !== value) return null;
  return <div className={cn('flex flex-col', className)}>{children}</div>;
};

export const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
});
